import {
  FAIcon,
  GenericLink,
  NextgenImage,
  Theme,
  useBreakpoint,
  IconStyle,
  LoaderType,
  getCdnAssetUrl,
} from '@nextgen-web-framework/all';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { getThemeColorClass, ThemeColor, ThemeShadesType } from '../../utils/themeColor';
import styles from '../ContentCard/ContentCard.module.scss';
import { Icons } from '../ContentCard/types/propsTypes';
import { ResourceType, type ResourceCardProps } from './types/propsTypes';

const ResourceCard = (props: ResourceCardProps) => {
  const viewPortType = useBreakpoint();
  const configs = ConfigService.getConfigs();
  const assetsBasePath = configs.NEXT_ASSETS_BASE_PATH as string;
  const bannerImage = props?.imageList?.find((item) => item?.viewport?.includes(viewPortType));

  //get theme color from props
  const themeColor = props.themeShade
    ? getThemeColorClass(ThemeColor.gray, props.themeShade as ThemeShadesType)
    : undefined;

  const theme = themeColor || `card-bg-color-${props.backgroundColor || 'white'}`;
  const txtColor = themeColor ? `card-color-black` : `card-color-${props.color || 'black'}`;

  const getIconName = (): IconName => {
    let iconName = '';
    switch (props.resourceType) {
      case ResourceType.image:
        iconName = Icons.Image;
        break;
      case ResourceType.pdf:
        iconName = Icons.PDF;
        break;
      case ResourceType.video:
        iconName = Icons.Video;
        break;

      case ResourceType.audio:
        iconName = Icons.Audio;
        break;
      default:
    }
    return iconName as IconName;
  };

  const iconName = getIconName();
  const isTargetLinkURL = props.link?.url ? props.link?.url : bannerImage?.url;

  const handleClick = () => {
    if (!props.detailPage) {
      const newWindowUrl =
        props.resourceType === ResourceType.image
          ? isTargetLinkURL
          : props.resourceType === ResourceType.pdf
            ? getCdnAssetUrl(props.link?.url ?? '')
            : props.link?.url;

      if (newWindowUrl) {
        window.open(newWindowUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const renderImage = () =>
    (props.resourceType === ResourceType.audio || bannerImage?.url) && (
      <NextgenImage
        src={getImageSrc()}
        alt={bannerImage?.altText ?? ''}
        className={styles['content-card-image']}
        loaderType={LoaderType.CONTENTSTACK}
        imageWidths={{
          small: 350,
          medium: 350,
          large: 480,
          xlarge: 480,
        }}
      />
    );

  const getImageSrc = () => {
    if (props.resourceType === ResourceType.audio) {
      return `${assetsBasePath}/images/MusicResource.svg`;
    } else {
      return bannerImage?.url ?? '';
    }
  };

  return (
    <div
      className={styles['content-card-link']}
      role="presentation"
      onClick={handleClick}
      onKeyDown={() => handleClick}
    >
      <div
        className={`${styles['content-card-container']}  ${styles['resource-card-container']} ${props.customClass}`}
        data-testid={`resource-${props.resourceType}-card-container-testid`}
      >
        <div className={styles['banner']}>
          {props.resourceType === ResourceType.pdf ? (
            <div className={styles['pdf-banner']} data-testid="resource-pdf-img-testid">
              <div className={styles['pdf-banner-wrapper']}>
                <div className={styles['pdf-banner-inner']}>
                  {bannerImage?.url && (
                    <NextgenImage
                      src={bannerImage?.url ?? ''}
                      alt={bannerImage?.altText ?? ''}
                      className={styles['content-card-image']}
                      loaderType={LoaderType.CONTENTSTACK}
                      imageWidths={{
                        small: 350,
                        medium: 350,
                        large: 480,
                        xlarge: 480,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              {props.detailPage ? (
                <GenericLink href={props.url ?? ''} target={props?.link?.target}>
                  {renderImage()}
                </GenericLink>
              ) : (
                <div>{renderImage()}</div>
              )}
            </>
          )}
          {iconName && (
            <div
              className={styles['icon-container']}
              data-testid="content-type-icon-testid"
              role="presentation"
              onClick={() => handleClick}
              onKeyDown={() => handleClick}
            >
              <FAIcon iconName={iconName} theme={Theme.WHITE} iconStyle={IconStyle.SOLID} />
            </div>
          )}

          {!props.detailPage && (
            <div
              className={styles['download-icon-container']}
              data-testid="download-icon-testid"
              role="presentation"
              onClick={() => handleClick}
              onKeyDown={() => handleClick}
            >
              <FAIcon iconName={'arrow-down-to-line'} theme={Theme.BLACK} />
            </div>
          )}
        </div>
        <div className={`${styles['content']} ${styles[theme]} ${styles['resource-card-content']}`}>
          <div className={`${styles['header']} ${styles[txtColor]}`}>
            <div className={`${styles['tag']}`}>{props.tag}</div>
            {props.title && (
              <div className={styles['title']} title={props.title}>
                {props.title}
              </div>
            )}
          </div>
          <div className={styles['footer']}>
            {props.subTitle && <div className={styles['footer-line']}>{props.subTitle}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;
export { ResourceCardProps };
